import { useCallback, useRef } from 'react'
import { cn } from '../utils/cn.ts'

export interface InputOTPProps {
  value: string
  onChange: (value: string) => void
  status?: 'default' | 'verifying' | 'error' | 'expired'
  message?: string
  onComplete?: (code: string) => void
  onResend?: () => void
}

export function InputOTP({
  value,
  onChange,
  status = 'default',
  message,
  onComplete,
  onResend,
}: InputOTPProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6)

  const isDisabled = status === 'verifying' || status === 'expired'
  const isMuted = status === 'verifying' || status === 'expired'
  const isError = status === 'error'

  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < 6) {
      inputsRef.current[index]?.focus()
    }
  }, [])

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (digits[index]) {
          // Clear current digit
          const newValue = value.slice(0, index) + value.slice(index + 1)
          onChange(newValue)
        } else if (index > 0) {
          // Move back and clear previous
          focusInput(index - 1)
        }
        return
      }

      // Only allow single digits
      if (/^\d$/.test(e.key)) {
        e.preventDefault()
        const newValue = value.slice(0, index) + e.key + value.slice(index + 1)
        const trimmed = newValue.slice(0, 6)
        onChange(trimmed)

        if (index < 5) {
          focusInput(index + 1)
        }

        if (trimmed.length === 6 && index === 5) {
          onComplete?.(trimmed)
        }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        // Block non-digit printable characters
        e.preventDefault()
      }
    },
    [value, digits, onChange, onComplete, focusInput],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
      if (pasted.length > 0) {
        onChange(pasted)
        if (pasted.length === 6) {
          onComplete?.(pasted)
          focusInput(5)
        } else {
          focusInput(pasted.length)
        }
      }
    },
    [onChange, onComplete, focusInput],
  )

  const renderMessage = () => {
    if (message) {
      return (
        <div
          className={cn('otp-message', isError && 'otp-message-error')}
          aria-live="polite"
        >
          {message}
        </div>
      )
    }

    switch (status) {
      case 'verifying':
        return (
          <div className="otp-message" aria-live="polite">
            Verifying...
          </div>
        )
      case 'error':
        return (
          <div className="otp-message otp-message-error" aria-live="polite">
            Incorrect code. Please try again.
          </div>
        )
      case 'expired':
        return (
          <div className="otp-message" aria-live="polite">
            Code expired.{' '}
            <button type="button" className="otp-resend" onClick={onResend}>
              Resend a new code
            </button>
          </div>
        )
      default:
        return (
          <div className="otp-message" aria-live="polite">
            {"Didn\u2019t receive a code? "}
            {onResend ? (
              <button type="button" className="otp-resend" onClick={onResend}>
                Resend
              </button>
            ) : null}
          </div>
        )
    }
  }

  return (
    <div className="otp-wrapper">
      <div className="otp-digits">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputsRef.current[index] = el }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            aria-label={`Digit ${index + 1} of 6`}
            disabled={isDisabled}
            readOnly={isDisabled}
            className={cn(
              'otp-digit',
              isError && 'otp-digit-error',
              isMuted && 'otp-digit-muted',
            )}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onChange={() => {}}
          />
        ))}
      </div>
      {renderMessage()}
    </div>
  )
}

InputOTP.displayName = 'InputOTP'
